// Admin authentication — Supabase Auth (email/password) with a clearly-flagged
// demo fallback so the dashboard remains usable before the Supabase admin user
// is email-confirmed. Disable by setting REACT_APP_ALLOW_DEMO_ADMIN=false.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { clearAdminSession, grantAdminSession } from "@/lib/adminSession";

const DEMO_KEY = "bcs_demo_admin";
const DEMO_EMAIL = "admin@bcsabha.org";
const DEMO_PASSWORD = "BCSAdmin@2026";
const ALLOW_DEMO = process.env.REACT_APP_ALLOW_DEMO_ADMIN === "true";

const AuthContext = createContext(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!mounted) return;
        if (session?.user) {
          setUser(session.user);
        } else if (ALLOW_DEMO && localStorage.getItem(DEMO_KEY)) {
          setUser({ email: localStorage.getItem(DEMO_KEY), demo: true });
          setIsDemo(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    init();

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        setIsDemo(false);
      } else if (!(ALLOW_DEMO && localStorage.getItem(DEMO_KEY))) {
        setUser(null);
      }
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error && data?.session) {
      setUser(data.session.user);
      setIsDemo(false);
      grantAdminSession();
      return { ok: true };
    }
    // Demo fallback — only when Supabase auth is not yet configured/confirmed.
    if (ALLOW_DEMO && email === DEMO_EMAIL && password === DEMO_PASSWORD) {
      localStorage.setItem(DEMO_KEY, email);
      setUser({ email, demo: true });
      setIsDemo(true);
      grantAdminSession();
      return { ok: true, demo: true };
    }
    return { ok: false, error: error?.message || "Invalid credentials" };
  }, []);

  const signOut = useCallback(async () => {
    clearAdminSession();
    localStorage.removeItem(DEMO_KEY);
    try {
      await supabase.auth.signOut();
    } catch (e) {
      /* ignore */
    }
    setUser(null);
    setIsDemo(false);
  }, []);

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      if (isDemo || user?.demo) {
        return {
          ok: false,
          error: "Password cannot be changed in demo mode. Sign in with a Supabase admin account.",
        };
      }

      const email = user?.email;
      if (!email) {
        return { ok: false, error: "You must be signed in to change your password." };
      }

      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (verifyError) {
        return { ok: false, error: "Current password is incorrect." };
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        return { ok: false, error: error.message || "Could not update password." };
      }

      grantAdminSession();
      return { ok: true };
    },
    [user, isDemo]
  );

  return (
    <AuthContext.Provider value={{ user, isDemo, loading, signIn, signOut, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}
